import { KPICard } from "@/components/accounting/kpi-card";
import { OrderStatusBadge } from "@/components/commerce/commerce-status-badge";
import { CommerceEmptyState } from "@/components/commerce/empty-state";
import {
	useDashboardOrders,
	useDashboardQuotes,
	useOrderAnalytics,
} from "@/hooks/use-commerce";
import { formatRelative } from "@/lib/format";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowRight,
	FileText,
	ShoppingCart,
	Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/commerce/")({
	component: CommerceDashboard,
});

function CommerceDashboard() {
	const { t } = useTranslation("commerce");
	const navigate = useNavigate();

	const { data: analytics, isLoading: analyticsLoading } =
		useOrderAnalytics(30);
	const { data: pendingQuotes } = useDashboardQuotes();
	const { data: recentOrdersData, isLoading: ordersLoading } =
		useDashboardOrders(8);

	const summary = analytics?.summary;
	const recentOrders = recentOrdersData?.orders ?? [];
	const pendingQuoteCount = pendingQuotes?.page?.totalCount ?? 0;

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* ── Row 1: KPI strip ── */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border-b shrink-0">
				{analyticsLoading ? (
					Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={`kpi-${i}`} className="h-[72px]" />
					))
				) : (
					<>
						<KPICard
							label={t("orders_30d", {
								defaultValue: "Заказы (30д)",
							})}
							amount={String(summary?.totalOrders ?? 0)}
							onClick={() =>
								navigate({ to: "/commerce/orders" })
							}
						/>
						<KPICard
							label={t("revenue_30d", {
								defaultValue: "Выручка (30д)",
							})}
							amount={String(
								Number(summary?.totalRevenueCents ?? 0) / 100,
							)}
							onClick={() =>
								navigate({ to: "/commerce/analytics" })
							}
						/>
						<KPICard
							label={t("avg_check", {
								defaultValue: "Средний чек",
							})}
							amount={String(
								Number(summary?.avgOrderValueCents ?? 0) / 100,
							)}
						/>
						<KPICard
							label={t("quotes_pending", {
								defaultValue: "КП в работе",
							})}
							amount={String(pendingQuoteCount)}
							onClick={() =>
								navigate({
									to: "/commerce/quotes",
									search: { view: "sent" },
								})
							}
						/>
					</>
				)}
			</div>

			{/* ── Row 2: Main content — 2-column layout ── */}
			<div className="flex-1 grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-0 overflow-auto">
				{/* Column 1: Action items */}
				<div className="flex flex-col border-r">
					<div className="flex items-center gap-1.5 px-3 py-2 border-b">
						<AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
						<span className="text-xs font-semibold">
							{t("pending_actions", {
								defaultValue: "Требуют внимания",
							})}
						</span>
					</div>
					<div className="divide-y">
						<ActionRow
							icon={
								<FileText className="h-3.5 w-3.5 text-muted-foreground" />
							}
							label={t("quotes_awaiting", {
								defaultValue: "КП ожидающие ответа",
							})}
							to="/commerce/quotes"
							search={{ view: "sent" }}
							count={pendingQuoteCount}
						/>
						<ActionRow
							icon={
								<ShoppingCart className="h-3.5 w-3.5 text-muted-foreground" />
							}
							label={t("draft_orders", {
								defaultValue: "Черновики заказов",
							})}
							to="/commerce/orders"
							count={
								recentOrders.filter((o) => o.isDraft).length
							}
						/>
						<ActionRow
							icon={
								<Truck className="h-3.5 w-3.5 text-muted-foreground" />
							}
							label={t("pending_shipments", {
								defaultValue: "Ожидают отгрузки",
							})}
							to="/commerce/shipments"
							count={0}
						/>
					</div>
				</div>

				{/* Column 2: Recent orders */}
				<div className="flex flex-col min-h-0">
					<div className="flex items-center justify-between px-3 py-2 border-b">
						<span className="text-xs font-semibold">
							{t("recent_orders", {
								defaultValue: "Последние заказы",
							})}
						</span>
						<Link
							to="/commerce/orders"
							className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
						>
							{t("all_orders", {
								defaultValue: "Все заказы",
							})}
							<ArrowRight className="h-3 w-3" />
						</Link>
					</div>
					{ordersLoading ? (
						<div className="p-3 space-y-2">
							{Array.from({ length: 5 }).map((_, i) => (
								<Skeleton
									key={`order-skel-${i}`}
									className="h-7 w-full"
								/>
							))}
						</div>
					) : recentOrders.length === 0 ? (
						<CommerceEmptyState
							title={t("no_orders_yet", {
								defaultValue: "Заказов пока нет",
							})}
							description={t("empty_description", {
								defaultValue: "Создайте первый заказ",
							})}
							actionLabel={t("new_order", {
								defaultValue: "Создать заказ",
							})}
							onAction={() =>
								navigate({ to: "/commerce/orders" })
							}
						/>
					) : (
						<div className="divide-y overflow-y-auto">
							{recentOrders.map((order) => (
								<Link
									key={order.id}
									to="/commerce/orders/$id"
									params={{ id: order.id }}
									className="flex items-center justify-between px-3 py-2 hover:bg-accent/50 transition-colors"
								>
									<div className="flex items-center gap-3 min-w-0">
										<span className="text-xs font-mono text-muted-foreground shrink-0">
											#{order.seqNo}
										</span>
										<span className="text-sm truncate">
											{order.title ||
												`Заказ #${order.seqNo}`}
										</span>
									</div>
									<div className="flex items-center gap-3 shrink-0">
										<OrderStatusBadge order={order} />
										<span className="text-xs text-muted-foreground w-20 text-right tabular-nums">
											{order.createdAt
												? formatRelative(
														new Date(
															Number(
																order.createdAt
																	.seconds,
															) * 1000,
														),
													)
												: "—"}
										</span>
									</div>
								</Link>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// ── Action row (same pattern as accounting) ──

function ActionRow({
	icon,
	label,
	to,
	search,
	count,
}: {
	icon: React.ReactNode;
	label: string;
	to: string;
	search?: Record<string, unknown>;
	count: number;
}) {
	return (
		<Link
			to={to}
			search={search as any}
			className="flex items-center gap-2 px-3 py-2.5 hover:bg-accent/50 transition-colors"
		>
			{icon}
			<span className="flex-1 text-xs">{label}</span>
			{count > 0 && (
				<span className="text-[10px] font-medium tabular-nums bg-muted rounded-full px-1.5 py-0.5">
					{count}
				</span>
			)}
			<ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
		</Link>
	);
}
